export function clampScheduleScale(value: number, min: number, max: number): number {
  'worklet';
  return Math.min(max, Math.max(min, value));
}

/** Continuous 0..1 progress for typography that follows a schedule card's height. */
export function getScheduleTextProgress(
  blockHeight: number,
  compactHeight: number,
  comfortableHeight: number,
): number {
  'worklet';
  const range = Math.max(1, comfortableHeight - compactHeight);
  return Math.min(1, Math.max(0, (blockHeight - compactHeight) / range));
}

/** Keep the time under the initial pinch midpoint anchored while zooming. */
export function getAnchoredScheduleScroll(
  startScroll: number,
  startFocalY: number,
  currentFocalY: number,
  startHourHeight: number,
  nextHourHeight: number,
  viewportHeight: number,
  topPadding: number,
  bottomPadding: number,
): number {
  'worklet';
  const focalHours = (startScroll + startFocalY - topPadding) / startHourHeight;
  const contentHeight = topPadding + 24 * nextHourHeight + bottomPadding;
  const maxScroll = Math.max(0, contentHeight - viewportHeight);
  const desiredScroll = topPadding + focalHours * nextHourHeight - currentFocalY;
  return Math.min(maxScroll, Math.max(0, desiredScroll));
}

/** Return the schedule column containing a horizontal point. */
export function getScheduleColumnAtX(columnWidths: number[], x: number): number {
  'worklet';
  if (columnWidths.length === 0) return 0;

  const clampedX = Math.max(0, x);
  let rightEdge = 0;
  for (let index = 0; index < columnWidths.length; index += 1) {
    rightEdge += Math.max(0, columnWidths[index] ?? 0);
    if (clampedX < rightEdge) return index;
  }
  return columnWidths.length - 1;
}

/** Resize one schedule column without compressing any of its neighbours. */
export function resizeScheduleColumns(
  columnWidths: number[],
  columnIndex: number,
  translationX: number,
  minimumWidth: number,
): number[] {
  if (
    columnWidths.length === 0
    || columnIndex < 0
    || columnIndex >= columnWidths.length
  ) return [...columnWidths];

  const next = [...columnWidths];
  const safeMinimum = Math.max(1, minimumWidth);
  next[columnIndex] = Math.max(safeMinimum, next[columnIndex] + translationX);
  return next;
}

/** Estimate a useful one-line width for a day column from its reminder labels. */
export function getScheduleAutoFitColumnWidth(
  labels: string[],
  minimumWidth: number,
  maximumWidth: number,
): number {
  const safeMinimum = Math.max(1, minimumWidth);
  const safeMaximum = Math.max(safeMinimum, maximumWidth);
  const longestLabel = labels.reduce(
    (longest, label) => Math.max(longest, label.trim().length),
    0,
  );
  if (longestLabel === 0) return safeMinimum;

  // Week cards use compact bold text. Six pixels per character plus card
  // insets gives a close, stable fit without measuring every native Text node.
  const estimatedWidth = 30 + Math.min(longestLabel, 40) * 6;
  return Math.min(safeMaximum, Math.max(safeMinimum, estimatedWidth));
}
